<template>
  <div class="shop-layout">
    <!-- Fixed backdrop: one image behind every shop page, generated for
         this shop (Qwen-Image — public/images/night-market-bg.webp).
         Decorative, never intercepts the pointer. The scrim over it is
         what keeps the UI readable: the plate's own fog is light in the
         middle, so content would otherwise sit on grey. -->
    <div class="shop-backdrop" aria-hidden="true"></div>
    <ShopHeader />
    <main class="shop-content">
      <slot />
    </main>
    <AppFooter />
    <ShopMasterPlayer />
    <ShopQueueDrawer />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { getShareImageUrl } from '~/utils/proxy'

/**
 * Storefront share/meta defaults.
 *
 * Set once here so EVERY shop URL carries them — before this the shop
 * emitted no og:* tags at all and links pasted into WhatsApp, Discord or
 * Instagram DMs rendered as a bare domain. Admins fill these in under
 * Settings → Shop Meta Tags (/api/admin/shop-meta/config); the values
 * ride along on /api/tenant/current.
 *
 * A page that sets its own head wins: its useHead registers after the
 * layout's, and Nuxt dedupes og/meta by property/name. Fields left blank
 * in the admin emit NO tag rather than an invented one — an empty
 * og:description is worse than none, and the global <title> from
 * nuxt.config should keep applying until an admin overrides it.
 */
const { tenant, appName } = useTenantTheme()
// Origin is stable for the life of the layout; the PATH is not. The shop
// layout survives client-side navigation, so reading the path off the
// captured request URL froze canonical/og:url on whichever page was
// server-rendered first — every in-app navigation then self-canonicalised
// to that one URL. useRoute() is reactive and re-evaluates per route.
const origin = useRequestURL().origin
const route = useRoute()

const shopMeta = computed(() => tenant.value?.shopMeta || {})
// route.path, not fullPath: query strings (utm_*, ?page=2) must not fork
// the canonical URL.
const canonical = computed(() => origin + route.path)

const headMeta = computed(() => {
  const m = shopMeta.value
  const shareTitle = m.ogTitle || m.title || appName.value
  const tags = [
    { property: 'og:type', content: 'website' },
    { property: 'og:site_name', content: appName.value },
    { property: 'og:title', content: shareTitle },
    { property: 'og:url', content: canonical.value }
  ]
  if (m.description) {
    tags.push({ name: 'description', content: m.description })
    tags.push({ property: 'og:description', content: m.description })
  }
  // Through the proxy and absolute — the admin stores the raw object URL, and
  // on a tenant whose bucket is not public that is a 403 to every crawler.
  // See getShareImageUrl.
  const shareImage = getShareImageUrl(m.ogImage, origin)
  if (shareImage) {
    tags.push({ property: 'og:image', content: shareImage })
    if (m.ogImageAlt) tags.push({ property: 'og:image:alt', content: m.ogImageAlt })
    // The card type only means something once there is an image to show.
    tags.push({ name: 'twitter:card', content: m.twitterCard || 'summary_large_image' })
    tags.push({ name: 'twitter:image', content: shareImage })
    tags.push({ name: 'twitter:title', content: shareTitle })
    if (m.description) tags.push({ name: 'twitter:description', content: m.description })
  }
  if (m.twitterSite) tags.push({ name: 'twitter:site', content: `@${m.twitterSite}` })
  return tags
})

useHead(() => ({
  // Blank leaves nuxt.config's global title in place.
  ...(shopMeta.value.title ? { title: shopMeta.value.title } : {}),
  meta: headMeta.value,
  link: [{ rel: 'canonical', href: canonical.value }]
}))
</script>

<style>
/* Footer copyright would otherwise hide under the 76px floating player
   when scrolled to the very bottom. Scoped to the shop layout so other
   layouts (admin/creator) — which mount the smaller GlobalAudioPlayer —
   keep their tighter footer padding. */
.shop-layout .app-footer {
  padding-bottom: calc(var(--space-6) + 110px);
}
</style>

<style scoped>
.shop-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--bg-color);
  color: var(--text-main);
}

.shop-backdrop {
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  /* Scrim first — the first background-image layer paints on top — then
     the plate. Near-opaque at the bottom where the long lists scroll,
     lighter at the top so the neon still reads behind the header. */
  background-image:
    linear-gradient(
      to bottom,
      rgb(3 3 4 / 0.78) 0%,
      rgb(3 3 4 / 0.88) 45%,
      rgb(3 3 4 / 0.97) 100%
    ),
    url('/images/night-market-bg.webp');
  background-size: cover, cover;
  background-position: center top, center top;
  background-repeat: no-repeat, no-repeat;
  background-attachment: scroll, fixed;
}

/* Phones: `background-attachment: fixed` is a known jank source on iOS
   Safari, and the plate is far wider than tall — let it scroll instead. */
@media (max-width: 600px) {
  .shop-backdrop { background-attachment: scroll, scroll; }
}

.shop-content {
  flex: 1;
  width: 100%;
  position: relative;
  z-index: 1;
  /* Single canonical content cap so every shop page aligns at the same
     edges. Width + horizontal padding mirror `.shop-header-inner` in
     `components/Shop/ShopHeader.vue` (max-width 1440px, padding 0 24px)
     EXACTLY — that way the page content starts flush with the logo on
     the left and ends flush with the cart/user buttons on the right.
     Auth-card pages (login/signup) center a `max-w-md` form INSIDE
     this frame — they don't need to override the cap. Inner pages
     that had their own `max-w-*` wrappers (1024/1152/etc.) have been
     dropped so they inherit this single source of truth. */
  max-width: 1440px;
  margin: 0 auto;
  /* Top clearance = shop chrome height (the 60px bar, see
     --cy-chrome-h in shop-cyber.css) + breathing room. Bottom padding
     doubles as float-clearance for ShopMasterPlayer: 76px player +
     18px shell padding + 26px breathing room so the last row of cards
     is never pinned under the floating bar. */
  padding: calc(var(--cy-chrome-h, 60px) + 24px) 24px 120px;
}

/* The footer must also paint above the fixed field. */
.shop-layout :deep(.app-footer) {
  position: relative;
  z-index: 1;
}

/* Tablet: tighten side padding so cards have more room. */
@media (max-width: 900px) {
  .shop-content { padding: calc(var(--cy-chrome-h, 60px) + 18px) 16px 110px; }
}

/* Phone: minimal side padding; the master player is shorter so we can
   trim bottom clearance too. */
@media (max-width: 600px) {
  .shop-content { padding: calc(var(--cy-chrome-h, 60px) + 12px) 12px 100px; }
}

@media (max-width: 380px) {
  .shop-content { padding: calc(var(--cy-chrome-h, 60px) + 10px) 10px 90px; }
}
</style>
